import { SCHEDULER_OFFLINE_ENABLED, hasOfflineScope } from '../../core/services/schedulerOfflineStore';
import { readOfflineShell } from '../../core/services/schedulerOfflineShell';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { User } from '../../core/models/data';

export type RulesAccessProfile = {
  authUid?: string;
  appUserId?: string;
  canonicalUserId?: string;
  sourceUserDocId?: string;
  role?: User['role'];
  unitIds?: string[];
  accessProfileVersion?: number;
  disabled?: boolean;
  suspended?: boolean;
  authorityStatus?: string;
  bindingGeneration?: number;
  schedulerReadAuthority?: { authorityGeneration?: string; roleGeneration?: string; status?: string; units?: Record<string, { policyGeneration?: string }> };
};

// The request and its published projection have the same owner. A new owner
// masks retained state during render, before effect cleanup/refetch can run.
export function useAppAccessProfile({ currentUser, firebaseAuthUid, isDemoMode, load, subscribe, normalizeUnits }: {
  currentUser: (User & { active?: boolean; isActive?: boolean }) | null;
  firebaseAuthUid: string | null;
  isDemoMode: boolean;
  load: (uid: string) => Promise<RulesAccessProfile | null>;
  subscribe?: (uid: string, next: (profile: RulesAccessProfile | null) => void, error: () => void) => () => void;
  normalizeUnits: (value: unknown) => string[];
}) {
  const [epoch, setEpoch] = useState(0);
  useEffect(() => {
    if (!SCHEDULER_OFFLINE_ENABLED || typeof window === 'undefined' || !window.addEventListener) return;
    const changed = () => setEpoch(n => n + 1);
    const names = ['online', 'offline', 'focus', 'mintleaf:scheduler:authority-changed'];
    names.forEach(name => window.addEventListener(name, changed));
    if (typeof document !== 'undefined') document.addEventListener?.('visibilitychange', changed);
    return () => { names.forEach(name => window.removeEventListener(name, changed)); if (typeof document !== 'undefined') document.removeEventListener?.('visibilitychange', changed); };
  }, []);
  const key = JSON.stringify([isDemoMode, firebaseAuthUid, currentUser?.id, currentUser?.authUid,
    currentUser?.accessProfileVersion ?? 0, currentUser?.role, currentUser?.active, currentUser?.isActive,
    currentUser ? normalizeUnits(currentUser).sort() : []]);
  const owner = useMemo(() => ({ key }), [key]);
  const liveOwner = useRef<typeof owner | null>(owner);
  liveOwner.current = owner;
  const [accepted, setAccepted] = useState<{
    owner: typeof owner; profile: RulesAccessProfile | null; blocked: boolean;
  } | null>(null);
  const latestAccepted = useRef(accepted); latestAccepted.current = accepted;
  const resetAccessProfile = useCallback(() => {
    liveOwner.current = null;
    setAccepted(null);
    setEpoch(value => value + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;
    // Connectivity is a refresh trigger, never a new identity boundary.
    setAccepted(previous => previous?.owner === owner ? previous : null);
    const publish = (profile: RulesAccessProfile | null, blocked: boolean) => {
      if (!cancelled && liveOwner.current === owner) setAccepted({ owner, profile, blocked });
    };
    if (isDemoMode) publish(null, false);
    else if (currentUser && firebaseAuthUid && currentUser.authUid === firebaseAuthUid && currentUser.active !== false && currentUser.isActive !== false) {
      if (SCHEDULER_OFFLINE_ENABLED && typeof navigator !== 'undefined' && !navigator.onLine) {
        const retained = latestAccepted.current;
        const effective = retained?.profile ? { ...currentUser,
          bindingGeneration: retained.profile.bindingGeneration,
          authorityGeneration: retained.profile.schedulerReadAuthority?.authorityGeneration,
          schedulerAuthorityVersion: retained.profile.schedulerReadAuthority ? JSON.stringify(retained.profile.schedulerReadAuthority) : undefined,
        } : currentUser;
        if (retained?.owner === owner && !retained.blocked && normalizeUnits(effective).some(id => hasOfflineScope(effective, [id]))) return () => { cancelled = true; };
        const shell = readOfflineShell(firebaseAuthUid);
        if (shell && shell.user.id === currentUser.id && shell.user.accessProfileVersion === currentUser.accessProfileVersion) publish(shell.profile, false);
        else publish(null, true);
        return () => { cancelled = true; };
      }
      const receive = (profile: RulesAccessProfile | null) => {
        // A freshly returned stored projection can itself be stale. It must
        // match the source which initiated this request, not replace it.
        const matches = !profile || (
          profile.authUid === firebaseAuthUid && profile.sourceUserDocId === currentUser.id &&
          (!profile.appUserId || profile.appUserId === currentUser.id) &&
          (!profile.canonicalUserId || profile.canonicalUserId === currentUser.id) &&
          (profile.accessProfileVersion ?? 0) === (currentUser.accessProfileVersion ?? 0) &&
          profile.role === currentUser.role &&
          JSON.stringify(normalizeUnits(profile).sort()) === JSON.stringify(normalizeUnits(currentUser).sort())
        );
        publish(matches ? profile : null, !matches || profile?.authorityStatus === 'denied' ||
          profile?.disabled === true || profile?.suspended === true);
      };
      // A failed transport cannot prove a permission revocation. Keep the
      // accepted projection, or remain unresolved on the first attempt.
      if (subscribe) unsubscribe = subscribe(firebaseAuthUid, receive, () => {});
      else void load(firebaseAuthUid).then(receive, () => {});
    } else if (currentUser) publish(null, true);
    return () => { cancelled = true; unsubscribe?.(); };
  }, [owner, epoch, load, subscribe, isDemoMode, normalizeUnits]);

  const candidate = accepted?.owner === owner ? accepted : null;
  const offlineActor = currentUser && candidate ? { ...currentUser,
    bindingGeneration: candidate.profile?.bindingGeneration,
    authorityGeneration: candidate.profile?.schedulerReadAuthority?.authorityGeneration,
    schedulerAuthorityVersion: candidate.profile?.schedulerReadAuthority ? JSON.stringify(candidate.profile.schedulerReadAuthority) : undefined,
  } : null;
  const current = SCHEDULER_OFFLINE_ENABLED && typeof navigator !== 'undefined' && !navigator.onLine &&
    candidate && (!offlineActor || !normalizeUnits(offlineActor).some(id => hasOfflineScope(offlineActor, [id])))
    ? { ...candidate, profile: null, blocked: true } : candidate;
  const accessProfile = current?.profile ?? null;
  const isAccessProfileLoaded = current !== null;
  const isAccessProfileBlocked = current?.blocked ?? false;
  // A confirmed absent legacy projection retains the existing P0 fallback.
  // Unresolved, failed or mismatched projections never activate that fallback.
  const authorizationUnitIds = useMemo(() => currentUser && current && !current.blocked
    ? normalizeUnits(current.profile ?? currentUser) : [], [currentUser, current, normalizeUnits]);
  const effectiveCurrentUser = useMemo(() => !currentUser || !current || current.blocked ? null
    : { ...currentUser, unitIds: authorizationUnitIds,
      bindingGeneration: current.profile?.bindingGeneration,
      authorityGeneration: current.profile?.schedulerReadAuthority?.authorityGeneration,
      schedulerAuthorityVersion: current.profile?.schedulerReadAuthority ? JSON.stringify(current.profile.schedulerReadAuthority) : undefined,
    }, [currentUser, current, authorizationUnitIds]);
  return { accessProfile, isAccessProfileLoaded, isAccessProfileBlocked, effectiveCurrentUser,
    authorizationUnitIds, resetAccessProfile };
}


